import api from '../utils/api';

/**
 * @typedef {Object} Review
 * @property {string} _id
 * @property {string} user
 * @property {string} reviewType - 'course', 'lesson', 'blog', 'instructor'
 * @property {string} reviewedItem
 * @property {number} rating
 * @property {string} comment
 * @property {Object[]} screenshots
 * @property {string} screenshots.url
 * @property {string} screenshots.publicId
 * @property {string} screenshots.caption
 * @property {boolean} isVerifiedPurchase
 * @property {boolean} isApproved
 * @property {boolean} isFlagged
 * @property {string} flagReason
 * @property {Object} helpfulVotes
 * @property {number} helpfulVotes.up
 * @property {number} helpfulVotes.down
 * @property {Object} adminReply
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef {Object} CreateReviewData
 * @property {string} reviewType
 * @property {string} itemId
 * @property {number} rating
 * @property {string} title
 * @property {string} content
 * @property {File[]} [images]
 */

export const reviewService = {
  // ==================
  // PUBLIC ROUTES
  // ==================

  /**
   * Get featured reviews
   * @returns {Promise<{status: string, reviews: Review[]}>}
   */
  getFeaturedReviews: async () => {
    const response = await api.get('/api/reviews/featured');
    return response;
  },

  /**
   * Get reviews for item
   * @param {string} reviewType 
   * @param {string} itemId 
   * @param {Object} [params] 
   * @returns {Promise<{status: string, count: number, reviews: Review[], averageRating: number}>}
   */
  getReviewsForItem: async (reviewType, itemId, params = {}) => {
    const response = await api.get(`/api/reviews/${reviewType}/${itemId}`, { params });
    return response;
  },

  // ==================
  // STUDENT ROUTES
  // ==================

  /**
   * Create or update review
   * @param {CreateReviewData} reviewData 
   * @returns {Promise<{status: string, review: Review}>}
   */
  createOrUpdateReview: async (reviewData) => {
    // Handle file upload
    const formData = new FormData();
    formData.append('reviewType', reviewData.reviewType);
    formData.append('itemId', reviewData.itemId);
    formData.append('rating', reviewData.rating);
    formData.append('title', reviewData.title);
    formData.append('content', reviewData.content);

    if (reviewData.images && reviewData.images.length > 0) {
      reviewData.images.forEach((image) => {
        formData.append('images', image);
      });
    }

    const response = await api.post('/api/reviews', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  /**
   * Get user's reviews
   * @param {string} userId 
   * @returns {Promise<{status: string, count: number, reviews: Review[]}>}
   */
  getUserReviews: async (userId) => {
    const response = await api.get(`/api/reviews/user/${userId}`);
    return response;
  },

  /**
   * Delete review
   * @param {string} reviewId 
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteReview: async (reviewId) => {
    const response = await api.delete(`/api/reviews/${reviewId}`);
    return response;
  },

  /**
   * Vote review as helpful
   * @param {string} reviewId 
   * @returns {Promise<{status: string, review: Review}>}
   */
  voteHelpful: async (reviewId) => {
    const response = await api.post(`/api/reviews/${reviewId}/vote`);
    return response;
  },

  // ==================
  // ADMIN ROUTES
  // ==================

  /**
   * Get all reviews (Admin)
   * @param {Object} [params] - Query parameters forwarded to the backend.
   * @param {boolean} [params.isAnonymous] - Filter by anonymity. `true` = anonymous only,
   *   `false` = authenticated only, omit the key entirely to fetch all.
   * @param {string} [params.reviewType] - 'course' | 'blog' | 'instructor'
   * @param {string} [params.isApproved] - 'true' | 'false'
   * @param {string} [params.isFlagged] - 'true' | 'false'
   * @param {number} [params.page]
   * @param {number} [params.limit]
   * @param {string} [params.search]
   * @returns {Promise<{status: string, count: number, reviews: Review[]}>}
   */
  getAllReviews: async (params = {}) => {
    const response = await api.get('/api/reviews/admin/all', { params });
    return response;
  },

  /**
   * Flag review (Admin)
   * @param {string} reviewId 
   * @param {string} reason 
   * @returns {Promise<{status: string, review: Review}>}
   */
  flagReview: async (reviewId, reason) => {
    const response = await api.put(`/api/reviews/${reviewId}/flag`, { reason });
    return response;
  },

  /**
   * Unflag review (Admin)
   * @param {string} reviewId 
   * @returns {Promise<{status: string, review: Review}>}
   */
  unflagReview: async (reviewId) => {
    const response = await api.put(`/api/reviews/${reviewId}/unflag`, { reason: 'Unflagged by admin' });
    return response;
  },
};
